import { Clock, ShieldOff, CalendarX, Ban, AlertTriangle } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { LoginScreen } from '@/components/LoginScreen'

const COPY: Record<string, { icon: typeof Clock; title: string; body: string }> = {
  pending: {
    icon: Clock,
    title: 'Tu cuenta todavía no tiene acceso a John Anthony',
    body: 'Contacta con el administrador para activar tu suscripción.',
  },
  suspended: {
    icon: ShieldOff,
    title: 'Tu cuenta está suspendida',
    body: 'Contacta con el administrador para más información.',
  },
  expired: {
    icon: CalendarX,
    title: 'Tu suscripción venció',
    body: 'Contacta con el administrador para renovarla.',
  },
  blocked: {
    icon: Ban,
    title: 'Tu cuenta está bloqueada',
    body: 'Contacta con el administrador para más información.',
  },
}

/** Pantalla mostrada cuando el usuario está autenticado pero no en estado 'active' — o
 * cuando la autenticación falló. Nunca se salta: el backend ya rechaza estas peticiones,
 * esta pantalla solo explica por qué. */
export function AccessGate({ status }: { status: string }) {
  const { error, retry } = useAuth()

  // P0.39 (27/08/2026) + P0.54 (29/08/2026, Sección 13 del encargo): abierta fuera de un
  // cliente Telegram real (navegador externo) -- ya NO es un dead-end ("vuelve a Telegram"),
  // ahora hay un camino real de login sin salir del navegador (Telegram Login Widget).
  if (status === 'needs_login') {
    return <LoginScreen />
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center gap-3 animate-fade-in-up motion-reduce:animate-none">
        <span className="h-16 w-16 rounded-2xl bg-warn-soft flex items-center justify-center">
          <AlertTriangle className="h-8 w-8 text-warn" />
        </span>
        <h1 className="text-base font-semibold">No se pudo conectar</h1>
        <p className="text-sm text-text-muted max-w-xs">{error}</p>
        <button
          onClick={retry}
          className="mt-2 px-4 py-2 rounded-lg bg-accent text-white text-sm font-medium"
        >
          Reintentar
        </button>
      </div>
    )
  }

  const copy = COPY[status] ?? COPY.pending
  const Icon = copy.icon

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center gap-3 animate-fade-in-up motion-reduce:animate-none">
      <span className="h-16 w-16 rounded-2xl bg-panel-elevated flex items-center justify-center">
        <Icon className="h-8 w-8 text-text-dim" />
      </span>
      <h1 className="text-base font-semibold">{copy.title}</h1>
      <p className="text-sm text-text-muted max-w-xs">{copy.body}</p>
    </div>
  )
}
